/**
 * Client HTTP de l'API exiPlan.
 *
 * Une seule couche d'accès réseau : toutes les erreurs remontent en `ApiError` avec le
 * message français renvoyé par le serveur, et un 401 est signalé explicitement pour que
 * la session puisse rebasculer vers l'écran de connexion sans que chaque appelant le gère.
 *
 * Porteur CIAM (lot 2 de la spec auth) : chaque appel attache `Authorization: Bearer` quand
 * MSAL rend un jeton (`getAccessToken`) et que l'appelant n'a pas explicitement demandé
 * `{ auth: false }` (D5 — routes publiques de partage/invitation, qui ne doivent jamais
 * porter de jeton, valide ou non). Le cookie de session hérité continue de voyager via
 * `credentials: 'include'` pendant la coexistence des deux modes (D7) ; il disparaît au lot 3.
 *
 * **Sauf** quand un compte MSAL est actif (`hasActiveAccount`, audit sécurité de la revue du
 * lot 2) : dans ce cas `credentials: 'omit'` — le cookie de session legacy ne doit jamais
 * réauthentifier une identité différente de celle portée par le jeton pendant la coexistence
 * (un onglet passé au porteur ne doit plus jamais retomber sur un vieux cookie d'un autre
 * compte). Sans compte MSAL actif (bypass, ou coexistence sans porteur), `'include'` reste le
 * comportement historique.
 */

import { getAccessToken, hasActiveAccount } from './auth.js';
import i18n from '../i18n/config.js';

/**
 * Base absolue de l'API, posée au build (`VITE_API_URL`). Front (Static Web App) et
 * serveur (App Service) sont déployés séparément, sans passerelle commune ni backend
 * lié à la Static Web App : le serveur a déjà `CORS_ORIGINS`/`CLIENT_ORIGIN` posés pour
 * accepter les requêtes de ce front. Vide par défaut → chemin relatif `/api`, seul cas
 * où c'est correct : le dev local, où le proxy Vite (`vite.config.js`) fait la jonction.
 * Le slash final est retiré pour ne jamais produire de double `//` avant `/api`.
 */
// Exportée : le flux de connexion (LoginPage) sort du contexte `fetch` pour naviguer
// en plein écran vers `/api/auth/:provider/start` (§ LoginPage.jsx) et a besoin de la
// même base absolue — sinon la navigation reste sur le domaine du front et 404.
export const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

/**
 * URL absolue du serveur MCP hébergé (transport HTTP Streamable), posée au build.
 * Vide en dev et en staging (pas de MCP de staging, § spec 2026-09-27 §2.2) : `McpHelpPanel`
 * n'affiche alors aucun extrait, et la page publique `/aide-mcp` retombe sur
 * `https://mcp.exiplan.fr/mcp`.
 */
export const MCP_URL = (import.meta.env.VITE_MCP_URL || '').replace(/\/$/, '');

/**
 * Client ID de l'inscription CIAM publique « ressource MCP » (`exiplan-mcp-cli`), pour la
 * commande `claude mcp add --client-id …` (standard MCP § 11, MCP-U1). Non secrète.
 */
export const MCP_CLI_CLIENT_ID = import.meta.env.VITE_MCP_CLI_CLIENT_ID || '';

export class ApiError extends Error {
  constructor(status, message, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
  get isUnauthenticated() {
    return this.status === 401;
  }
  /** Code applicatif du serveur (`details.code`), `undefined` s'il n'en pose pas. */
  get code() {
    return this.details?.code;
  }
  /** 403 `SEAT_REQUIRED` : membre actif sans siège exiPlan (mode `registre`, lot O3). */
  get isSeatRequired() {
    return this.status === 403 && this.code === 'SEAT_REQUIRED';
  }
  /** 403 `MSAL_REQUIS` : geste de gestion refusé hors connexion Microsoft interactive. */
  get isMsalRequis() {
    return this.status === 403 && this.code === 'MSAL_REQUIS';
  }
  /** 503 : registre du studio indisponible ou occupé — lecture seule, jamais une déconnexion. */
  get isRegistreIndisponible() {
    return (
      this.status === 503 &&
      (this.code === 'REGISTRE_INDISPONIBLE' || this.code === 'REGISTRE_OCCUPE')
    );
  }
}

/** Abonnés notifiés à la première réponse 401 — la session s'y branche. */
const unauthenticatedHandlers = new Set();
export const onUnauthenticated = (handler) => {
  unauthenticatedHandlers.add(handler);
  return () => unauthenticatedHandlers.delete(handler);
};

/**
 * Code d'erreur qui doit remonter au seul appelant plutôt que déclencher la déconnexion
 * globale : un 401 `REAUTH_REQUIRED` (§ suppression d'organisation) signale une session valide
 * mais trop ancienne pour un geste irréversible — ce n'est pas une session expirée, et
 * `onUnauthenticated` ne doit ni déconnecter l'utilisateur ni le rediriger.
 */
const NON_DISCONNECTING_401_CODES = new Set(['REAUTH_REQUIRED']);

/** Code de refus de rôle (§ 3.5.3) — un renouvellement forcé peut le faire disparaître. */
const APP_ROLE_PENDING = 'APP_ROLE_PENDING';

/** Construit les en-têtes d'une requête, en attachant le porteur MSAL si l'appel le veut. */
async function buildHeaders(body, auth) {
  const headers = body === undefined ? {} : { 'content-type': 'application/json' };
  if (!auth) return { headers, hadToken: false };
  const token = await getAccessToken().catch(() => null);
  if (!token) return { headers, hadToken: false };
  return { headers: { ...headers, authorization: `Bearer ${token}` }, hadToken: true };
}

function doFetch(method, path, { body, signal, headers }) {
  return fetch(`${API_BASE}/api${path}`, {
    method,
    // Coexistence (D7) : le cookie de session voyage tant que le chemin cookie n'est pas
    // retiré (lot 3) — SAUF si un compte MSAL est actif, auquel cas il est explicitement omis
    // (audit sécurité, revue du lot 2) : voir le commentaire d'en-tête de ce fichier.
    credentials: hasActiveAccount() ? 'omit' : 'include',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
}

/**
 * Un seul renouvellement forcé puis un seul rejeu, jamais plus — évite une boucle si le
 * serveur répond encore la même chose une fois le jeton renouvelé (§ 3.5.3, `lib/api.js`).
 */
async function retryWithForcedRefresh(method, path, { body, signal, headers }) {
  const refreshed = await getAccessToken({ forceRefresh: true }).catch(() => null);
  if (!refreshed) return null;
  return doFetch(method, path, {
    body,
    signal,
    headers: { ...headers, authorization: `Bearer ${refreshed}` },
  });
}

async function request(method, path, { body, signal, raw = false, auth = true } = {}) {
  const { headers, hadToken } = await buildHeaders(body, auth);
  let response = await doFetch(method, path, { body, signal, headers });

  if (raw) {
    if (!response.ok) {
      if (response.status === 401) unauthenticatedHandlers.forEach((handler) => handler());
      throw new ApiError(response.status, i18n.t('api.downloadFailed'));
    }
    return response;
  }

  let payload = await response.json().catch(() => null);

  // Un jeton peut avoir été révoqué ou renouvelé en silence entre deux requêtes : un seul
  // renouvellement forcé puis un rejeu (D9, § 3.5.3) — jamais pour `REAUTH_REQUIRED`, qu'un
  // simple renouvellement silencieux ne peut pas satisfaire (il exige une interaction).
  if (hadToken && response.status === 401 && payload?.error?.details?.code !== 'REAUTH_REQUIRED') {
    const retried = await retryWithForcedRefresh(method, path, { body, signal, headers });
    if (retried) {
      response = retried;
      payload = await response.json().catch(() => null);
    }
  } else if (
    hadToken &&
    response.status === 403 &&
    payload?.error?.details?.code === APP_ROLE_PENDING &&
    payload?.error?.details?.granted === true
  ) {
    const retried = await retryWithForcedRefresh(method, path, { body, signal, headers });
    if (retried) {
      response = retried;
      payload = await response.json().catch(() => null);
    }
  }

  if (response.status === 401 && !NON_DISCONNECTING_401_CODES.has(payload?.error?.details?.code)) {
    unauthenticatedHandlers.forEach((handler) => handler());
  }
  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.error?.message || i18n.t('api.genericError', { status: response.status }),
      payload?.error?.details,
    );
  }
  return payload;
}

export const api = {
  get: (path, options) => request('GET', path, options),
  post: (path, body, options) => request('POST', path, { ...options, body }),
  patch: (path, body, options) => request('PATCH', path, { ...options, body }),
  put: (path, body, options) => request('PUT', path, { ...options, body }),
  delete: (path, options) => request('DELETE', path, options),

  /**
   * Déclenche le téléchargement d'un fichier produit par l'API (CSV, PDF).
   * Passe par `fetch` plutôt que par un `<a download>` direct : le cookie de session
   * et les erreurs HTTP sont ainsi traités comme partout ailleurs.
   */
  async download(path, filename) {
    const response = await request('GET', path, { raw: true });
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};
